import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
// material
import {
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import { filter } from 'lodash';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

//
import { useFormik } from 'formik';
import {
  DeviceListHead,
  DeviceListToolbar,
  DeviceMoreMenu,
  DeviceMoreInfo
} from '../components/device';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { TableFilterSidebar } from '../components/sidebarFilter';
import { useTranslation } from '../common/LocalizationProvider';
import {
  selectDevicesArray,
  selectGroupSelectedId,
  selectGroupsArray,
  selectSession,
  selectPositionsArray
} from '../createSelectorUtils';
// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      array,
      (_device) =>
        String(_device.name).toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        String(_device.uniqueId).toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        String(_device.phone).indexOf(query) !== -1 ||
        (_device.club
          ? String(_device.club).toLowerCase().indexOf(query.toLowerCase()) !== -1
          : false)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function Device() {
  const t = useTranslation();
  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const [openFilter, setOpenFilter] = useState(false);

  const hiddenDown = useMediaQuery((theme) => theme.breakpoints.between('xs', 'sm'));

  const [tableHead, setTableHead] = useState([]);

  useEffect(() => {
    const INITIAL_TABLE_HEAD = () => {
      if (session.administrator) {
        if (hiddenDown) {
          return [
            { id: 'name', label: t('fields_Name_Device'), alignRight: false },
            {
              id: 'uniqueId',
              label: t('fields_Indicative'),
              alignRight: false
            },
            { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
            { id: '' }
          ];
        }
        return [
          { id: 'name', label: t('fields_Name_Device'), alignRight: false },
          {
            id: 'uniqueId',
            label: t('fields_Indicative'),
            alignRight: false
          },
          { id: 'phone', label: t('fields_Phone'), alignRight: false },
          { id: 'club', label: t('fields_Club'), alignRight: false },
          { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
          { id: '' }
        ];
      }

      if (groupId === 0) {
        if (hiddenDown) {
          return [
            { id: 'name', label: t('fields_Name_Device'), alignRight: false },
            {
              id: 'uniqueId',
              label: t('fields_Indicative'),
              alignRight: false
            },
            { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
            { id: '' }
          ];
        }
        return [
          { id: 'name', label: t('fields_Name_Device'), alignRight: false },
          {
            id: 'uniqueId',
            label: t('fields_Indicative'),
            alignRight: false
          },
          { id: 'phone', label: t('fields_Phone'), alignRight: false },
          { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
          { id: '' }
        ];
      }

      if (hiddenDown) {
        return [
          { id: 'name', label: t('fields_Name_Device'), alignRight: false },
          { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
          { id: '' }
        ];
      }
      return [
        { id: 'name', label: t('fields_Name_Device'), alignRight: false },
        { id: 'phone', label: t('fields_Phone'), alignRight: false },
        { id: 'orometer', label: t('fields_Orometer'), alignRight: false },
        { id: '' }
      ];
    };

    setTableHead(INITIAL_TABLE_HEAD());
  }, [groupId, hiddenDown, session.administrator, t]);

  const formik = useFormik({
    initialValues: {
      club: null,
      device: null
    },
    onSubmit: () => {
      setOpenFilter(false);
    }
  });
  const { resetForm, handleSubmit } = formik;

  const handleOpenFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleResetFilter = () => {
    handleSubmit();
    resetForm();
  };

  // ----------------------------------------------------------------------
  const groups = useSelector(selectGroupsArray);
  const selectDevices = useSelector(selectDevicesArray);

  const devices = (() => {
    if (formik.values.device != null) {
      return selectDevices.filter((item) => item.id === formik.values.device.id);
    }

    if (formik.values.club != null) {
      return selectDevices.filter((item) => item.groupId === formik.values.club.id);
    }

    if (session.administrator) {
      return selectDevices;
    }

    if (groupId === 0) {
      return selectDevices.filter(
        (item) =>
          item.groupId === 0 ||
          session.permission.find((per) => per.groupid === item.groupId) === undefined
      );
    }

    return selectDevices.filter((item) => item.groupId === groupId);
  })().map((item) => {
    const group = groups.find((group) => group.id === item.groupId);
    return {
      ...item,
      club: group === undefined ? '' : group.name
    };
  });

  const positions = useSelector(selectPositionsArray);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - devices.length) : 0;

  const filteredDevices = applySortFilter(devices, getComparator(order, orderBy), filterName);

  const isDeviceNotFound = filteredDevices.length === 0;

  return (
    <Page title={t('DL_Title')}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {t('devicesList')}
          </Typography>
          {(session.administrator ||
            (groupId === 0 && devices.filter((device) => device.groupId === 0) < 1)) && (
            <Button
              variant="contained"
              component={RouterLink}
              to="add"
              startIcon={<Icon icon={plusFill} />}
            >
              {t('DL_NewDevice')}
            </Button>
          )}
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap-reverse"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ mb: 5 }}
        >
          {session.administrator && (
            <Stack direction="row" spacing={1} flexShrink={0} sx={{ my: 1 }}>
              <TableFilterSidebar
                formik={formik}
                isOpenFilter={openFilter}
                onResetFilter={handleResetFilter}
                onOpenFilter={handleOpenFilter}
                onCloseFilter={handleCloseFilter}
                clubFilter
              />
            </Stack>
          )}
        </Stack>

        <Card>
          <DeviceListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 300 }}>
              <Table>
                <DeviceListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableHead}
                  rowCount={devices.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredDevices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { id, disabled } = row;

                      return (
                        <TableRow
                          hover
                          key={id}
                          sx={{ backgroundColor: disabled ? 'rgba(0, 0, 0, 0.2)' : 'transparent' }}
                        >
                          <DeviceMoreInfo
                            device={row}
                            administrator={session.administrator}
                            groupId={groupId}
                            positions={positions}
                            hiddenDown={hiddenDown}
                          />

                          {!!(
                            permission.status === 0 ||
                            permission.status === 1 ||
                            session.administrator ||
                            groupId === 0
                          ) && (
                            <TableCell align="right">
                              <DeviceMoreMenu id={id} />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={8} />
                    </TableRow>
                  )}
                </TableBody>
                {isDeviceNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 50, 100, 250, 500]}
            component="div"
            count={filteredDevices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
